import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Trophy, Calendar, User, Shield, Loader2, AlertCircle } from "lucide-react";
import { Button } from "./components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_equestrian-hub-20/artifacts/0lyoa6pm_No%20hatter%2C%20feher.png";

export default function ResultApproval() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approvalData, setApprovalData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [completedAction, setCompletedAction] = useState(null);
  
  const approvalToken = searchParams.get("token");
  const eventId = searchParams.get("event");
  
  useEffect(() => {
    const fetchApprovalData = async () => {
      if (!approvalToken || !eventId) {
        setError("Érvénytelen jóváhagyási link");
        setLoading(false);
        return;
      }
      
      try {
        const res = await axios.get(`${API}/competition/approval-data/${eventId}?approval_token=${approvalToken}`);
        setApprovalData(res.data);
      } catch (err) {
        console.error("Failed to fetch approval data", err);
        if (err.response?.status === 400) {
          setError(err.response.data?.detail || "Ez a jóváhagyási link már fel lett használva");
        } else if (err.response?.status === 404) {
          setError("A versenyszám nem található");
        } else {
          setError("Hiba történt az adatok betöltésekor");
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchApprovalData();
  }, [approvalToken, eventId]);
  
  const handleAction = async (action) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/competition/events/${eventId}/approve-results`, {
        competition_id: approvalData.competition_id,
        action: action,
        approval_token: approvalToken,
        approver_email: approvalData.approver_email
      });
      
      setCompleted(true);
      setCompletedAction(action);
      toast.success(action === "approve" ? "Eredmény elfogadva!" : "Eredmény elutasítva!");
    } catch (err) {
      console.error("Failed to submit action", err);
      toast.error(err.response?.data?.detail || "Hiba történt");
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Adatok betöltése...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Hiba</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate("/verseny")} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            Vissza a versenyekhez
          </Button>
        </div>
      </div>
    );
  }
  
  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            completedAction === "approve" ? "bg-emerald-100" : "bg-red-100"
          }`}>
            {completedAction === "approve" ? (
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            ) : (
              <XCircle className="w-8 h-8 text-red-600" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">
            {completedAction === "approve" ? "Eredmény elfogadva!" : "Eredmény elutasítva!"}
          </h1>
          <p className="text-slate-600 mb-6">
            {completedAction === "approve" 
              ? "Az eredményeket sikeresen elfogadtad." 
              : "Az eredményeket elutasítottad. A bíró értesítést kap."}
          </p>
          <Button onClick={() => navigate("/verseny")} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            Vissza a versenyekhez
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Gepárd Logo" className="w-20 h-20 mx-auto mb-4 opacity-90" />
          <h1 className="text-3xl font-bold text-white mb-2">Eredmény elfogadása</h1>
        </div>
        
        {/* Info Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
          {/* Approver Info */}
          <div className="bg-indigo-50 p-6 border-b border-indigo-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center">
                  <User size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Elfogadó neve</p>
                  <p className="font-semibold text-slate-800">{approvalData?.approver_name || "N/A"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                  <Shield size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Szerepkör</p>
                  <p className="font-semibold text-slate-800">{approvalData?.approver_role || "N/A"}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Competition Info */}
          <div className="p-6 border-b border-slate-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
                  <Trophy size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Verseny neve</p>
                  <p className="font-semibold text-slate-800">{approvalData?.competition_name || "N/A"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-600 rounded-full flex items-center justify-center">
                  <Calendar size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Versenyszám</p>
                  <p className="font-semibold text-slate-800">
                    {approvalData?.event_number}. {approvalData?.event_name}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Results Table */}
          <div className="p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Eredmények</h3>
            {approvalData?.results?.length === 0 ? (
              <p className="text-slate-500 text-center py-4">Nincsenek még rögzített eredmények</p>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left p-3 text-sm font-semibold text-slate-700 border-b">Helyezés</th>
                    <th className="text-left p-3 text-sm font-semibold text-slate-700 border-b">Lovas</th>
                    <th className="text-left p-3 text-sm font-semibold text-slate-700 border-b">Ló</th>
                    {/* Hiba-idő, Alapidő columns */}
                    {(approvalData?.judging_type === 'hiba_ido' || approvalData?.judging_type === 'alapido' || !approvalData?.judging_type) && (
                      <>
                        <th className="text-right p-3 text-sm font-semibold text-slate-700 border-b">Hibapont</th>
                        <th className="text-right p-3 text-sm font-semibold text-slate-700 border-b">Idő</th>
                      </>
                    )}
                    {/* Díjlovaglás columns */}
                    {approvalData?.judging_type === 'dijlovaglas' && (
                      <>
                        <th className="text-right p-3 text-sm font-semibold text-slate-700 border-b">Pont</th>
                        <th className="text-right p-3 text-sm font-semibold text-slate-700 border-b">%</th>
                      </>
                    )}
                    {/* Stílus columns */}
                    {approvalData?.judging_type === 'stilus' && (
                      <>
                        <th className="text-right p-3 text-sm font-semibold text-slate-700 border-b">Stíluspont</th>
                        <th className="text-right p-3 text-sm font-semibold text-slate-700 border-b">Idő</th>
                      </>
                    )}
                    {/* Két fázis columns */}
                    {approvalData?.judging_type === 'ket_fazis' && (
                      <>
                        <th className="text-right p-3 text-sm font-semibold text-slate-700 border-b">I. Fázis</th>
                        <th className="text-right p-3 text-sm font-semibold text-slate-700 border-b">II. Fázis</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(approvalData?.results || []).map((result, index) => (
                    <tr key={index} className={`border-b ${index < 3 ? 'bg-amber-50' : ''}`}>
                      <td className="p-3">
                        {result.status ? (
                          <span className="text-red-600 font-bold text-sm">{result.status}</span>
                        ) : (
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                            result.place === 1 ? 'bg-yellow-400 text-yellow-900' :
                            result.place === 2 ? 'bg-slate-300 text-slate-700' :
                            result.place === 3 ? 'bg-amber-600 text-white' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {result.place || '-'}
                          </span>
                        )}
                      </td>
                      <td className="p-3 font-medium text-slate-800">{result.rider_name}</td>
                      <td className="p-3 text-slate-600">{result.horse_name}</td>
                      {/* Hiba-idő, Alapidő values */}
                      {(approvalData?.judging_type === 'hiba_ido' || approvalData?.judging_type === 'alapido' || !approvalData?.judging_type) && (
                        <>
                          <td className="p-3 text-right font-mono text-slate-700">{result.penalties ?? '-'}</td>
                          <td className="p-3 text-right font-mono text-slate-700">{result.time ? `${result.time.toFixed(2)}s` : '-'}</td>
                        </>
                      )}
                      {/* Díjlovaglás values */}
                      {approvalData?.judging_type === 'dijlovaglas' && (
                        <>
                          <td className="p-3 text-right font-mono text-slate-700">{result.score ?? '-'}</td>
                          <td className="p-3 text-right font-mono text-slate-700">{result.percentage ? `${result.percentage.toFixed(2)}%` : '-'}</td>
                        </>
                      )}
                      {/* Stílus values */}
                      {approvalData?.judging_type === 'stilus' && (
                        <>
                          <td className="p-3 text-right font-mono text-slate-700">{result.score ?? '-'}</td>
                          <td className="p-3 text-right font-mono text-slate-700">{result.time ? `${result.time.toFixed(2)}s` : '-'}</td>
                        </>
                      )}
                      {/* Két fázis values */}
                      {approvalData?.judging_type === 'ket_fazis' && (
                        <>
                          <td className="p-3 text-right font-mono text-slate-700">
                            {result.phase1_penalty != null ? `${result.phase1_penalty} / ${result.phase1_time?.toFixed(2)}s` : '-'}
                          </td>
                          <td className="p-3 text-right font-mono text-slate-700">
                            {result.phase2_penalty != null ? `${result.phase2_penalty} / ${result.phase2_time?.toFixed(2)}s` : '-'}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="p-6 bg-slate-50 border-t border-slate-100">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => handleAction("approve")}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 text-lg"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-5 h-5 mr-2" />
                )}
                Elfogadás
              </Button>
              <Button
                onClick={() => handleAction("reject")}
                disabled={submitting}
                variant="outline"
                className="border-red-500 text-red-600 hover:bg-red-50 px-8 py-3 text-lg"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <XCircle className="w-5 h-5 mr-2" />
                )}
                Elutasítás
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
